import React, { useEffect, useState, useRef, useCallback, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import EditorToolbar from './documents/EditorToolbar';
import _ from 'lodash';

/**
 * Word and text documents in the viewer.
 *
 * TWO CONTRACTS THIS COMPONENT KEEPS (files-4):
 *  1. It does NOT scroll. The DOCX iframe is grown to the height of its own
 *     content and the whole editor lays out at its natural height, so the one
 *     scrollbar in the viewer belongs to the full-width region above it and
 *     sits at the page's right edge - not in a 860px gutter beside the page.
 *  2. Zoom is a CSS `zoom` on the page surface (files-3), which scales the
 *     rendered document without re-flowing the iframe's own layout, so a Word
 *     document at 200% is the same document, larger.
 */
interface UnifiedDocumentEditorProps {
  content: string;
  fileType: 'docx' | 'text';
  onContentChange: (content: string) => void;
  onSave?: () => void;
  /** Reading zoom factor, 1 = 100%. */
  zoom?: number;
}

const UnifiedDocumentEditor = React.forwardRef<HTMLDivElement, UnifiedDocumentEditorProps>(
  ({ content, fileType, onContentChange, onSave, zoom = 1 }, ref) => {
    const { t } = useTranslation();
    const editorRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const docxFrameRef = useRef<HTMLIFrameElement | null>(null);
    const [docxHeight, setDocxHeight] = useState<number>(600);
    // Natural content width of the DOCX. 0 = "not measured yet / fits the page",
    // in which case the page keeps its 860px reading width. A wider value (a wide
    // table, a landscape page) grows the frame so the content is reachable by
    // scrolling right instead of being clipped.
    const [docxWidth, setDocxWidth] = useState<number>(0);
    const [history, setHistory] = useState<{ content: string; selection: Range | null }[]>([{ content, selection: null }]);
    const [historyIndex, setHistoryIndex] = useState<number>(0);
    const [currentBlockType, setCurrentBlockType] = useState<string>('Normal text');
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isUnorderedList, setIsUnorderedList] = useState(false);
    const [isOrderedList, setIsOrderedList] = useState(false);

    // Forward the internal ref to the parent component
    useImperativeHandle(ref, () => editorRef.current as HTMLDivElement);

    // Initialize editor content
    useEffect(() => {
      // Skip for DOCX files - they render in iframe
      if (fileType === 'docx') {
        return;
      }

      if (editorRef.current && content) {

        editorRef.current.innerHTML = content;
        editorRef.current.focus();

        // Log what the editor actually rendered
      }
    }, [content, fileType]);

    // Save selection utility
    const saveSelection = useCallback((): Range | null => {
      const selection = window.getSelection();
      return selection && selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;
    }, []);

    // Restore selection utility
    const restoreSelection = useCallback((range: Range | null) => {
      if (range) {
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }, []);

    // Get block ancestor helper
    const getBlockAncestor = useCallback((node: Node): HTMLElement | null => {
      let current: Node | null = node;
      while (current && current !== editorRef.current) {
        if (current.nodeType === 1) {
          const tag = (current as HTMLElement).tagName.toLowerCase();
          if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) return current as HTMLElement;
        }
        current = current.parentNode;
      }
      return null;
    }, []);

    // Update formatting states
    const updateFormattingStates = useCallback(() => {
      setIsBold(document.queryCommandState('bold'));
      setIsItalic(document.queryCommandState('italic'));
      setIsUnderline(document.queryCommandState('underline'));
      setIsUnorderedList(document.queryCommandState('insertUnorderedList'));
      setIsOrderedList(document.queryCommandState('insertOrderedList'));
    }, []);

    // Update block type
    const updateBlockType = useCallback(
      _.debounce(() => {
        if (!editorRef.current) return;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const anchorNode = selection.anchorNode;
        if (!anchorNode) return;
        const blockElement = getBlockAncestor(anchorNode);
        if (blockElement) {
          const tag = blockElement.tagName.toLowerCase();
          setCurrentBlockType(tag.startsWith('h') && tag.length === 2 ? `Header ${tag[1]}` : 'Normal text');
        } else {
          setCurrentBlockType('Normal text');
        }
      }, 100),
      [getBlockAncestor]
    );

    // Update history with debounce
    const debouncedUpdateHistory = useCallback(
      _.debounce(() => {
        if (!editorRef.current) return;
        const currentContent = editorRef.current.innerHTML;
        const selection = saveSelection();
        if (currentContent !== history[historyIndex]?.content) {
          const newHistory = history.slice(0, historyIndex + 1);
          newHistory.push({ content: currentContent, selection });
          setHistory(newHistory);
          setHistoryIndex(newHistory.length - 1);
        }
      }, 300),
      [history, historyIndex, saveSelection]
    );

    // Handle input
    const handleInput = useCallback(
      (e: React.FormEvent<HTMLDivElement>) => {
        const newContent = (e.target as HTMLDivElement).innerHTML;
        onContentChange(newContent);
        debouncedUpdateHistory();
      },
      [onContentChange, debouncedUpdateHistory]
    );

    // Undo/Redo
    const undo = useCallback(() => {
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        if (editorRef.current) {
          editorRef.current.innerHTML = history[newIndex].content;
          restoreSelection(history[newIndex].selection);
          onContentChange(history[newIndex].content);
        }
      }
    }, [historyIndex, history, restoreSelection, onContentChange]);

    const redo = useCallback(() => {
      if (historyIndex < history.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        if (editorRef.current) {
          editorRef.current.innerHTML = history[newIndex].content;
          restoreSelection(history[newIndex].selection);
          onContentChange(history[newIndex].content);
        }
      }
    }, [historyIndex, history, restoreSelection, onContentChange]);

    // Keyboard shortcuts
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        const isMac = navigator.platform.toUpperCase().includes('MAC');
        const modKey = isMac ? e.metaKey : e.ctrlKey;

        // Handle Cmd+S / Ctrl+S for saving at document level
        if (modKey && e.key.toLowerCase() === 's') {
          e.preventDefault();
          if (onSave) onSave();
          return;
        }

        // Only apply other shortcuts if editor is focused
        if (editorRef.current && editorRef.current.contains(document.activeElement)) {
          if (modKey) {
            switch (e.key.toLowerCase()) {
              case 'b':
                e.preventDefault();
                document.execCommand('bold', false, undefined);
                updateFormattingStates();
                break;
              case 'i':
                e.preventDefault();
                document.execCommand('italic', false, undefined);
                updateFormattingStates();
                break;
              case 'u':
                e.preventDefault();
                document.execCommand('underline', false, undefined);
                updateFormattingStates();
                break;
              case 'z':
                e.preventDefault();
                undo();
                break;
              case 'y':
                e.preventDefault();
                redo();
                break;
              default:
                break;
            }
          }
        }
      };

      const editor = editorRef.current;
      if (editor) {
        editor.addEventListener('keyup', updateFormattingStates);
        editor.addEventListener('click', updateFormattingStates);
        document.addEventListener('selectionchange', updateFormattingStates);
      }

      // Attach keyboard listener to document for global Cmd+S/Ctrl+S support
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        if (editor) {
          editor.removeEventListener('keyup', updateFormattingStates);
          editor.removeEventListener('click', updateFormattingStates);
          document.removeEventListener('selectionchange', updateFormattingStates);
        }
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [undo, redo, onSave, updateFormattingStates]);

    // Block type updates
    useEffect(() => {
      const editor = editorRef.current;
      if (editor) {
        editor.addEventListener('click', updateBlockType);
        editor.addEventListener('keyup', updateBlockType);
        document.addEventListener('selectionchange', updateBlockType);
      }
      return () => {
        if (editor) {
          editor.removeEventListener('click', updateBlockType);
          editor.removeEventListener('keyup', updateBlockType);
          document.removeEventListener('selectionchange', updateBlockType);
        }
      };
    }, [updateBlockType]);

    /*
      Grow the DOCX iframe to its content (files-4).

      The iframe used to be pinned to 90vh with `overflow: auto`, so the
      document scrolled INSIDE a 860px-wide box and the bar the reader reached
      for sat against the paper rather than against the window. Measuring the
      rendered body and handing that height to the frame makes the frame as
      tall as the document, leaving the scrolling to the full-width region
      above it. `srcDoc` is same-origin, so the measurement is a plain read.
    */
    const docxObserverRef = useRef<ResizeObserver | null>(null);

    const measureDocx = useCallback(() => {
      const frame = docxFrameRef.current;
      const body = frame?.contentDocument?.body;
      const docEl = frame?.contentDocument?.documentElement;
      if (!body) return;
      body.style.margin = '0';
      // Clip the frame's OWN vertical overflow (we grow the frame to fit), but let
      // horizontal content through - the page scrolls horizontally in the viewer,
      // not inside the frame, so nothing wide gets cut off at the frame edge.
      body.style.overflowY = 'hidden';
      body.style.overflowX = 'visible';
      const height = Math.max(body.scrollHeight, docEl?.scrollHeight || 0);
      if (height > 0) setDocxHeight(height);
      const width = Math.max(body.scrollWidth, docEl?.scrollWidth || 0);
      // Only grow past the reading width when the content genuinely needs it.
      if (width > 861) setDocxWidth(width);

      // Late reflows - web fonts, images - happen after load, so keep watching
      // the body rather than trusting the one measurement at onLoad.
      if (!docxObserverRef.current && typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(() => {
          const el = docxFrameRef.current?.contentDocument?.body;
          if (!el) return;
          if (el.scrollHeight > 0) setDocxHeight(el.scrollHeight);
          if (el.scrollWidth > 861) setDocxWidth(el.scrollWidth);
        });
        observer.observe(body);
        docxObserverRef.current = observer;
      }
    }, []);

    // A new document means a new body to watch, and a fresh width to measure.
    useEffect(() => {
      docxObserverRef.current?.disconnect();
      docxObserverRef.current = null;
      setDocxWidth(0);
    }, [content]);

    useEffect(() => () => docxObserverRef.current?.disconnect(), []);

    return (
      <div className="flex flex-col w-full min-h-full bg-surface">

        {/* Show toolbar only for text files (not for read-only DOCX) */}
        {fileType === 'text' && (
          <EditorToolbar
            editorRef={editorRef}
            isBold={isBold}
            isItalic={isItalic}
            isUnderline={isUnderline}
            isUnorderedList={isUnorderedList}
            isOrderedList={isOrderedList}
            currentBlockType={currentBlockType}
            onFormatChange={() => {
              updateFormattingStates();
              updateBlockType();
              debouncedUpdateHistory();
            }}
          />
        )}

        {/*
          Page surface - natural height, zoomed as a whole, never its own scroller.

          Centering is `margin: 0 auto` on the page, NOT flexbox `justify-content:
          center`. With flex-center, a page wider than the viewer (a zoomed-in
          document, a wide table, a landscape page) overflows BOTH sides and the
          left/right edges become unreachable - you physically cannot scroll to
          them. With auto margins the page centers while it fits and, once it is
          wider than the viewer, pins to the left and overflows only to the right,
          so the viewer's horizontal scrollbar reaches all of it.
        */}
        <div
          ref={containerRef}
          style={{ width: '100%', zoom }}
        >
          {fileType === 'docx' ? (
            // DOCX renders in a same-origin iframe grown to its content height AND
            // width, so wide content is reachable by scrolling right rather than
            // clipped. `allow-same-origin` alone: no scripts run in the HTML.
            <div
              style={{
                width: docxWidth > 0 ? `${docxWidth}px` : '860px',
                maxWidth: docxWidth > 0 ? 'none' : '100%',
                margin: '0 auto',
              }}
            >
              <iframe
                ref={docxFrameRef}
                title={t('DOCX document')}
                srcDoc={content}
                sandbox="allow-same-origin"
                onLoad={measureDocx}
                scrolling="no"
                style={{
                  width: '100%',
                  height: `${docxHeight}px`,
                  border: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'block',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff',
                }}
              />
            </div>
          ) : (
            // HTML/Text editor - display EXACTLY as the document defines it
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              style={{
                width: '100%',
                padding: '2rem',
                outline: 'none',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}
              onInput={handleInput}
            />
          )}
        </div>
      </div>
    );
  }
);

UnifiedDocumentEditor.displayName = 'UnifiedDocumentEditor';

export default UnifiedDocumentEditor;
